import type { AdminIdentity, AdminPlanNote, AdminWorkspace, PlanNote, ResponsePlanSummary, CatalogItem, CatalogResponse, ContractRow, ContractSigner, CrmOption, CrmRecordKind, DealDetails, DealFollowUpDraft, DealQualificationDraft, FollowUpWarning, OrderForm, PricingModel, StripeBillingPreview, PublicWorkspace, SharedSandboxAccess, TimelineEvent, WorkspaceListPage, WorkspacePhase, WorkspaceQuote } from '../types'
import type { CustomerDetails } from './order-form'

async function readResponsePayload(response: Response): Promise<Record<string, unknown>> {
  const raw = await response.text().catch(() => '')
  if (!raw) return {}
  try {
    const parsed: unknown = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {}
  } catch {
    return {}
  }
}

export interface StandardDocumentStatus {
  source: 'uploaded' | 'builtin'
  meta: { fileName: string; byteSize: number; uploadedBy: string; uploadedAt: string } | null
  url: string
}

/** The Prentus signer's saved signature. The image itself is at SAVED_SIGNATURE_URL (admin session only). */
export interface SavedSignatureStatus { signerEmail: string; signerName: string; uploadedBy: string; uploadedAt: string; byteSize: number; matchesSigner: boolean }
export const SAVED_SIGNATURE_URL = '/api/admin/contract-signer/signature'

export interface OrderFormInvoiceLinks {
  number: string | null
  status: string | null
  hostedInvoiceUrl: string
  invoicePdf: string | null
  amountDue: number | null
  dueDate: string | null
}

export async function request<T>(path: string, init?: RequestInit, unexpectedResponseMessage = 'Something went wrong.'): Promise<T> {
  const hasJsonBody = Boolean(init?.body) && !(init?.body instanceof FormData)
  const response = await fetch(path, {
    credentials: 'same-origin',
    cache: 'no-store',
    ...init,
    headers: {
      ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })
  const data = await readResponsePayload(response)
  if (!response.ok) {
    const error = new Error(typeof data.error === 'string' ? data.error : unexpectedResponseMessage) as Error & { status?: number; code?: string; warnings?: FollowUpWarning[] }
    error.status = response.status
    if (typeof data.code === 'string') error.code = data.code
    if (Array.isArray(data.warnings)) error.warnings = data.warnings
    throw error
  }
  return data as T
}

export const api = {
  findWorkspace(email: string) {
    return request<{ kind: 'admin'; redirectTo: string } | { kind: 'customer'; slug: string } | { kind: 'partner'; slug: string }>('/api/access/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },
  loadAdminWorkspaces(params: { page?: number; pageSize?: number; search?: string; status?: 'draft' | 'published' } = {}) {
    const query = new URLSearchParams()
    if (params.page) query.set('page', String(params.page))
    if (params.pageSize) query.set('pageSize', String(params.pageSize))
    if (params.search) query.set('search', params.search)
    if (params.status) query.set('status', params.status)
    const suffix = query.toString()
    return request<WorkspaceListPage>(`/api/admin/workspaces${suffix ? `?${suffix}` : ''}`)
  },
  searchCrm(kind: CrmRecordKind, query: string) {
    return request<{ results: CrmOption[] }>('/api/admin/crm/search', {
      method: 'POST',
      body: JSON.stringify({ kind, query }),
    })
  },
  loadDealDetails(dealId: string) {
    return request<DealDetails>('/api/admin/crm/deal-details', {
      method: 'POST',
      body: JSON.stringify({ dealId }),
    })
  },
  createWorkspaceFromDeal(payload: { dealId: string; primaryCompanyId: string; companyIds: string[]; contactIds: string[] }) {
    return request<{ workspaceId: string; publicId: string; publicUrl: string; status: string; callsImported: number }>('/api/admin/workspaces/from-deal', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
  createWorkspaceFromCompany(payload: { companyId: string; companyName: string; companyDomain?: string; phase?: WorkspacePhase; contacts: Array<{ id?: string; email: string; name?: string; title?: string }> }) {
    return request<{ workspaceId: string; publicId: string; publicUrl: string; status: string; callsImported: number }>('/api/admin/workspaces/from-company', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
  loadWorkspace(publicId: string) {
    return request<PublicWorkspace>(`/api/workspaces/${encodeURIComponent(publicId)}`)
  },
  requestCode(publicId: string, email: string) {
    return request<{ ok: true }>(`/api/workspaces/${encodeURIComponent(publicId)}/access/request`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },
  verifyCode(publicId: string, email: string, code: string) {
    return request<PublicWorkspace>(`/api/workspaces/${encodeURIComponent(publicId)}/access/verify`, {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    })
  },
  track(publicId: string, eventType: string, targetId?: string) {
    return request<{ ok: true }>(`/api/workspaces/${encodeURIComponent(publicId)}/events`, {
      method: 'POST',
      body: JSON.stringify({ eventType, targetId }),
    })
  },
  reportOrderFormActivity(publicId: string, formId: string, type: 'viewed' | 'signing_started') {
    return request<{ ok: true }>(`/api/workspaces/${encodeURIComponent(publicId)}/order-forms/${encodeURIComponent(formId)}/activity`, {
      method: 'POST',
      body: JSON.stringify({ type }),
    })
  },
  requestSandbox(publicId: string, context: string) {
    return request<{ id: string; status: 'pending' }>(`/api/workspaces/${encodeURIComponent(publicId)}/sandbox-requests`, {
      method: 'POST',
      body: JSON.stringify({ context }),
    })
  },
  /** Response plan notes for the Response Plan tab. canSend is true only for a verified customer. */
  listPlanNotes(publicId: string) {
    return request<{ notes: PlanNote[]; canSend: boolean }>(`/api/workspaces/${encodeURIComponent(publicId)}/response-plan/notes`)
  },
  sendPlanNote(publicId: string, sectionKey: string, body: string) {
    return request<{ note: PlanNote }>(`/api/workspaces/${encodeURIComponent(publicId)}/response-plan/notes`, {
      method: 'POST',
      body: JSON.stringify({ sectionKey, body }),
    }, 'The note could not be sent.')
  },
  loadAdminResponsePlan(workspaceId: string) {
    return request<{ plan: ResponsePlanSummary | null; notes: AdminPlanNote[]; linearKeyConfigured: boolean }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/response-plan`)
  },
  uploadResponsePlan(workspaceId: string, file: File) {
    const form = new FormData()
    form.set('file', file)
    return request<{ plan: ResponsePlanSummary }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/response-plan`, { method: 'PUT', body: form }, 'The plan could not be uploaded.')
  },
  removeResponsePlan(workspaceId: string) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/response-plan`, { method: 'DELETE' })
  },
  syncPlanNotes(workspaceId: string) {
    return request<{ synced: number; pending: number }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/response-plan/sync`, { method: 'POST' })
  },
  loadSandboxAccess(publicId: string) {
    return request<SharedSandboxAccess>(`/api/workspaces/${encodeURIComponent(publicId)}/sandbox-access`)
  },
  generateQuote(publicId: string, pricingModel: PricingModel, calculatorInputs: Record<string, unknown>) {
    return request<WorkspaceQuote>(`/api/workspaces/${encodeURIComponent(publicId)}/quotes`, {
      method: 'POST',
      body: JSON.stringify({ requestId: crypto.randomUUID(), pricingModel, calculatorInputs }),
    })
  },
  recordSession(publicId: string, payload: {
    action: 'start' | 'heartbeat' | 'end'
    sessionId: string
    sectionKey?: string
    durationMs?: number
    click?: { type: 'asset_open' | 'asset_download'; targetId: string }
  }) {
    return request<{ ok: true }>(`/api/workspaces/${encodeURIComponent(publicId)}/session`, {
      method: 'POST',
      body: JSON.stringify(payload),
      keepalive: true,
    })
  },
  loadCatalog() {
    return request<CatalogResponse>('/api/admin/catalog')
  },
  saveCatalog(items: CatalogItem[]) {
    return request<CatalogResponse>('/api/admin/catalog', { method: 'PUT', body: JSON.stringify(items) })
  },
  sendOrderFormForSignature(workspaceId: string, formId: string, payload: { cc: string[]; note: string | null; emailCustomer: boolean }) {
    return request<{ orderForm: OrderForm; ccNotified: boolean; senderSigns: boolean; prentusEmailed: boolean; autoSigned: boolean; customerEmailed: boolean }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/order-forms/${encodeURIComponent(formId)}/send`, { method: 'POST', body: JSON.stringify(payload) })
  },
  /** The customer corrects their address, billing contact or PO number on a form out for signature. */
  updateOrderFormDetails(publicId: string, formId: string, details: Partial<CustomerDetails>) {
    return request<{ orderForm: OrderForm; changed: boolean; autoSigned?: boolean }>(`/api/workspaces/${encodeURIComponent(publicId)}/order-forms/${encodeURIComponent(formId)}/customer-details`, { method: 'POST', body: JSON.stringify(details) })
  },
  remindOrderFormSigner(workspaceId: string, formId: string) {
    return request<{ orderForm: OrderForm }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/order-forms/${encodeURIComponent(formId)}/remind`, { method: 'POST', body: '{}' })
  },
  /** Cancel and Edit (draft) or Void. Cancels the DocuSeal request first. */
  withdrawOrderForm(workspaceId: string, formId: string, status: 'draft' | 'void') {
    return request<{ orderForm: OrderForm }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/order-forms/${encodeURIComponent(formId)}/withdraw`, { method: 'POST', body: JSON.stringify({ status }) })
  },
  /** Whether Stripe is connected, and to the test or live account. Admin only. */
  loadStripeStatus() {
    return request<{ connected: boolean; mode: 'test' | 'live' | null }>('/api/admin/stripe/status')
  },
  /** Retry Stripe Setup on a signed form: runs only the steps that have not run yet. */
  retryStripeSetup(workspaceId: string, formId: string) {
    return request<{ orderForm: OrderForm }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/order-forms/${encodeURIComponent(formId)}/stripe/retry`, { method: 'POST', body: '{}' })
  },
  /** Stripe Billing check: what a setup would do for this form now. Stripe GET requests only; creates nothing. */
  loadStripePreview(workspaceId: string, formId: string) {
    return request<{ preview: StripeBillingPreview }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/order-forms/${encodeURIComponent(formId)}/stripe/preview`)
  },
  /** Active Stripe products for the product picker. */
  loadStripeProducts() {
    return request<{ products: Array<{ id: string; name: string }> }>('/api/admin/stripe/products')
  },
  /** Saves an admin's pick on the form: a Stripe product for a line, or the Stripe customer. Writes nothing to Stripe. */
  saveStripeSelection(workspaceId: string, formId: string, selection: { lineId: string; productId: string | null } | { customerId: string | null }) {
    return request<{ orderForm: OrderForm }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/order-forms/${encodeURIComponent(formId)}/stripe/selection`, { method: 'PUT', body: JSON.stringify(selection) })
  },
  /** The customer's invoice links for a signed form, once Stripe has issued the invoice. */
  loadOrderFormInvoice(publicId: string, formId: string) {
    return request<{ invoice: OrderFormInvoiceLinks | null }>(`/api/workspaces/${encodeURIComponent(publicId)}/order-forms/${encodeURIComponent(formId)}/invoice`)
  },
  loadStandardDocuments() {
    return request<{ csa: StandardDocumentStatus; signer: ContractSigner; signature: SavedSignatureStatus | null }>('/api/admin/documents')
  },
  saveSignature(png: string) {
    return request<{ signature: SavedSignatureStatus }>(SAVED_SIGNATURE_URL, { method: 'PUT', body: JSON.stringify({ png }) })
  },
  removeSignature() {
    return request<{ signature: null }>(SAVED_SIGNATURE_URL, { method: 'DELETE' })
  },
  uploadStandardCsa(file: File) {
    const form = new FormData()
    form.set('file', file)
    return request<{ csa: StandardDocumentStatus }>('/api/admin/documents/csa', { method: 'PUT', body: form, headers: {} })
  },
  saveContractSigner(signer: ContractSigner) {
    return request<{ signer: ContractSigner }>('/api/admin/contract-signer', { method: 'PUT', body: JSON.stringify(signer) })
  },
  removeStandardCsa() {
    return request<{ csa: StandardDocumentStatus }>('/api/admin/documents/csa', { method: 'DELETE' })
  },
  loadContracts() {
    return request<{ contracts: ContractRow[] }>('/api/admin/contracts')
  },
  setContractSharing(workspaceId: string, formId: string, shareSignedCopy: boolean) {
    return request<{ orderForm: OrderForm }>(`/api/admin/contracts/${encodeURIComponent(workspaceId)}/${encodeURIComponent(formId)}`, { method: 'PATCH', body: JSON.stringify({ shareSignedCopy }) })
  },
  /** Deletes an order form. A signed form needs its number as `confirmNumber`. */
  deleteContract(workspaceId: string, formId: string, confirmNumber?: string) {
    return request<{ ok: true }>(`/api/admin/contracts/${encodeURIComponent(workspaceId)}/${encodeURIComponent(formId)}`, { method: 'DELETE', body: JSON.stringify(confirmNumber ? { confirmNumber } : {}) })
  },
  loadAdminIdentity() {
    return request<AdminIdentity>('/api/admin/me')
  },
  /** Ends the admin session on the server and clears the admin cookie. */
  signOutAdmin() {
    return request<{ ok: true }>('/api/admin/auth/logout', { method: 'POST', body: '{}' })
  },
  addMember(workspaceId: string, payload: { side: 'customer' | 'prentus'; name: string; title: string | null; email: string; stakeholderRole: string | null }) {
    return request<{ id: string }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/members`, { method: 'POST', body: JSON.stringify(payload) })
  },
  setMemberHidden(workspaceId: string, memberId: string, hidden: boolean) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/members/${encodeURIComponent(memberId)}`, { method: 'PATCH', body: JSON.stringify({ hidden }) })
  },
  loadAdminWorkspace(workspaceId: string) {
    return request<AdminWorkspace>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}`)
  },
  updateAdminWorkspace(workspaceId: string, patch: Record<string, unknown>) {
    return request<AdminWorkspace>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  },
  decideApproval(workspaceId: string, payload: { itemType: 'asset' | 'business_case' | 'task' | 'suggestion' | 'sandbox_request'; itemId: string; decision: 'approve' | 'hide' }) {
    return request<AdminWorkspace>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/approvals`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
  addCall(workspaceId: string, payload: { title: string; occurredAt: string; recordingUrl: string | null; description: string | null }) {
    return request<AdminWorkspace>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/calls`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
  deleteWorkspaceItem(workspaceId: string, payload: { itemType: 'business_case' | 'task' | 'quote' | 'asset'; itemId: string }) {
    return request<{ ok: true }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/items`, {
      method: 'DELETE',
      body: JSON.stringify(payload),
    })
  },
  deleteWorkspace(workspaceId: string, payload: { accountName: string }) {
    return request<{ ok: true }>(
      `/api/admin/workspaces/${encodeURIComponent(workspaceId)}`,
      { method: 'DELETE', body: JSON.stringify(payload) },
      'Workspace deletion returned an unexpected response. Please try again.',
    )
  },
  reviewWorkspace(workspaceId: string) {
    return request<{ workspace: AdminWorkspace; reviewedAt: string; model: string; added: number; peopleAdded: number; warnings: string[] }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/review`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },
  generateCollateral(workspaceId: string, suggestionId: string) {
    return request<{ workspace: AdminWorkspace; assetId: string; version: number }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/collateral/${encodeURIComponent(suggestionId)}/generate`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },
  qualifyWorkspace(workspaceId: string, qualification: DealQualificationDraft, existingDealId?: string) {
    return request<{ workspace: AdminWorkspace; dealId: string; gmailDraftId: string; gmailDraftsUrl: string; warnings?: FollowUpWarning[] }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/qualify`, {
      method: 'POST',
      body: JSON.stringify({
        id: qualification.id,
        dealName: qualification.dealName,
        dealStage: qualification.dealStage,
        contacts: qualification.contacts,
        email: qualification.email,
        ...(existingDealId ? { existingDealId } : {}),
        // Worker recomputes this from the calculators server-side rather than
        // trusting it outright, per the registerWorkspaceQuoteRoutes-adjacent
        // recompute in the qualify route. Sent as a starting point + the
        // admin's own override signal.
        ...(qualification.estimatedAmount !== undefined ? { estimatedAmount: qualification.estimatedAmount } : {}),
        ...(qualification.pricingModel ? { pricingModel: qualification.pricingModel } : {}),
        ...(qualification.estimateSource ? { estimateSource: qualification.estimateSource } : {}),
      }),
    })
  },
  createFollowupDraft(workspaceId: string, followUp: DealFollowUpDraft) {
    return request<{ workspace: AdminWorkspace; gmailDraftId: string; gmailDraftsUrl: string; warnings?: FollowUpWarning[] }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/followup-draft`, {
      method: 'POST',
      body: JSON.stringify({ id: followUp.id, contacts: followUp.contacts, email: followUp.email }),
    })
  },
  loadTimeline(workspaceId: string, cursor?: string, source?: string) {
    const query = new URLSearchParams()
    if (cursor) query.set('cursor', cursor)
    if (source && source !== 'all') query.set('source', source)
    return request<{ events: TimelineEvent[]; nextCursor: string | null }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/timeline?${query}`)
  },
  upload(workspaceId: string, kind: 'logo' | 'asset', file: File, details?: { title?: string; description?: string; projectId?: string; section?: string; orderFormId?: string }) {
    const form = new FormData()
    form.set('kind', kind)
    form.set('file', file)
    if (details?.title) form.set('title', details.title)
    if (details?.description) form.set('description', details.description)
    if (details?.projectId) form.set('projectId', details.projectId)
    if (details?.section) form.set('section', details.section)
    if (details?.orderFormId) form.set('orderFormId', details.orderFormId)
    return request<{ id?: string; url: string; visibility?: 'pending' | 'published' }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/uploads`, {
      method: 'POST',
      body: form,
      headers: {},
    })
  },
  createDemoCredentials(workspaceId: string, payload: { title: string; description: string | null; loginUrl: string; username: string; password: string }) {
    return request<{ id: string; visibility: 'pending' }>(`/api/admin/workspaces/${encodeURIComponent(workspaceId)}/demo-credentials`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
}
